import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, AlertTriangle, Droplets, Zap, KeyRound, Wind, Wrench, Clock4, ShieldCheck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, SERVICE_BY_SLUG, HERO_IMAGE } from "@/lib/data";
import useSeo from "@/hooks/useSeo";

const URGENT_CATEGORIES = [
  { icon: Droplets, t: "Burst pipes & flooding", b: "Mains leak, hot water unit blown, ceiling dripping. We'll have a plumber on the way.", img: SERVICE_BY_SLUG.plumbing.image },
  { icon: Zap, t: "Power outages & faults", b: "Switchboard tripped repeatedly, exposed wiring, no power to half the house.", img: SERVICE_BY_SLUG.electrical.image },
  { icon: KeyRound, t: "Lockouts & break-ins", b: "Locked out, lock damaged or you need it changed today after an incident.", img: SERVICE_BY_SLUG.locksmithing.image },
  { icon: Wind, t: "Storm & roof damage", b: "Tile gone, gutter detached, water coming in — we can tarp and patch first, then quote the repair.", img: SERVICE_BY_SLUG.roofing.image },
  { icon: Wrench, t: "Hot water failure", b: "No hot water in the middle of winter? We carry common HWS units and can usually swap same-day.", img: SERVICE_BY_SLUG.plumbing.image },
  { icon: AlertTriangle, t: "Commercial site faults", b: "Retail tenancy faults, doors that won't lock at close, alarm and CCTV failures.", img: SERVICE_BY_SLUG["cctv-antennas-cabling"].image },
];

export default function Emergency() {
  useSeo({
    title: "Emergency Repairs — RSR Australia",
    description: "Urgent property repairs — burst pipes, no power, lockouts, hot water failure. 7-day urgent line. Call 0410 850 550.",
  });
  return (
    <>
      {/* Hero — dramatic dark with image */}
      <section data-testid="emergency-hero" className="relative bg-foreground text-background border-b border-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover duotone opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/90 to-foreground/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-transparent" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-6 border border-accent/40 bg-accent/10 px-3 py-1.5">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Urgent line · 7 days</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] tracking-tighter leading-[0.95] font-extrabold text-white">
              Urgent property repair? <br /> <span className="text-accent">We've got you.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-background/80">
              Same-day response for genuine emergencies — burst pipes, no power, no lock, no hot water. Skip the form. Just call.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-white">
                <Clock4 className="w-4 h-4 text-accent" /> 5-min triage
              </div>
              <div className="flex items-center gap-2 text-white">
                <ShieldCheck className="w-4 h-4 text-accent" /> Licensed & insured
              </div>
              <div className="flex items-center gap-2 text-white">
                <Phone className="w-4 h-4 text-accent" /> Someone always picks up
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="emergency-hero-call"
              className="group block bg-accent hover:bg-accent/90 text-white p-7 lg:p-8 transition-colors"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold tracking-[0.28em] uppercase opacity-80">Tap to call now</span>
                  <span className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">{PHONE_DISPLAY}</span>
                </div>
                <Phone className="w-9 h-9 transition-transform group-hover:scale-110" />
              </div>
              <div className="mt-5 pt-5 border-t border-white/20 text-[11px] uppercase tracking-[0.22em] opacity-80">
                Hold tight — we'll triage on the call →
              </div>
            </a>
            <p className="mt-4 text-xs text-background/60">Standard call-out fees may apply for after-hours response. Quoted up-front before dispatch.</p>
          </div>
        </div>
      </section>

      {/* When to call us — with images */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">When to ring the urgent line</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Things that <br /> can't wait until Monday.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">If it's water, power, locks or weather damage — call us straight away. We'll triage on the phone and dispatch the right trade.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {URGENT_CATEGORIES.map((c, i) => (
              <div key={i} data-testid={`emergency-cat-${i}`} className="relative group bg-card border-r border-b border-border overflow-hidden flex flex-col">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground/60 via-foreground/20 to-transparent" />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-accent text-white flex items-center justify-center">
                    <c.icon className="w-6 h-6" strokeWidth={1.7} />
                  </div>
                  <div className="absolute bottom-4 right-4 font-display text-2xl font-extrabold text-white drop-shadow tabular-nums">0{i + 1}</div>
                </div>
                <div className="p-7 flex flex-col gap-3 flex-1">
                  <h3 className="font-display text-xl font-bold tracking-tight">{c.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed urgent band */}
      <section className="relative h-[36vh] sm:h-[42vh] overflow-hidden">
        <img src={SERVICE_BY_SLUG.electrical.image} alt="" className="absolute inset-0 w-full h-full object-cover duotone" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/70 to-foreground/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-8 flex-wrap">
            <div className="text-white max-w-xl">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Don't wait</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Every minute counts <br /> with water and power.
              </h2>
            </div>
            <a
              href={`tel:${PHONE_TEL}`}
              className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-6 py-4 font-display text-xl sm:text-2xl font-bold tracking-tighter transition-colors"
            >
              <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Response times */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-5">
            <p className="overline">Response times</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              Speed you can <br /> actually plan around.
            </h2>
            <p className="mt-6 text-base text-muted-foreground max-w-md">
              We triage every urgent call within minutes and confirm an ETA before we hang up. No "we'll call you back tomorrow" — you know exactly when help arrives.
            </p>
            <div className="mt-8 relative aspect-[4/3] overflow-hidden border border-border bg-secondary hidden lg:block">
              <img src={SERVICE_BY_SLUG.plumbing.image} alt="A plumber working on urgent repairs" className="w-full h-full object-cover duotone" />
              <div className="absolute inset-0 bg-gradient-to-tr from-foreground/30 to-transparent" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7 space-y-px">
            {[
              { k: "Triage call", v: "Within 5 minutes of you ringing", desc: "We assess the issue on the phone and dispatch the right trade." },
              { k: "Same-day", v: "Genuine emergencies, business hours", desc: "Burst pipes, no power, lockouts — usually on-site inside a few hours." },
              { k: "After-hours", v: "Weeknights & weekends", desc: "Reduced crew but always on-call for urgent jobs. Standard call-out fee applies." },
              { k: "Public holidays", v: "Skeleton on-call roster", desc: "Genuine emergencies covered. Best-effort response — usually same-day." },
            ].map((r, i) => (
              <div key={i} data-testid={`emergency-response-${i}`} className="flex items-start gap-5 bg-background border border-border p-6">
                <Clock4 className="w-5 h-5 text-accent flex-shrink-0 mt-1" strokeWidth={1.7} />
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-5 items-start">
                  <div className="overline">{r.k}</div>
                  <div className="font-display text-base sm:text-lg font-bold tracking-tight">{r.v}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Big repeating CTA */}
      <section className="py-16 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="emergency-big-call"
            className="group relative overflow-hidden flex items-center justify-between gap-6 bg-accent hover:bg-accent/90 text-white px-7 py-7 lg:py-10 transition-colors"
          >
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative flex flex-col">
              <span className="text-[10px] font-bold tracking-[0.28em] uppercase opacity-80">Don't wait — call now</span>
              <span className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-2">{PHONE_DISPLAY}</span>
            </div>
            <div className="relative flex items-center gap-4">
              <Phone className="w-10 h-10 transition-transform group-hover:scale-110 hidden sm:block" />
              <ArrowUpRight className="w-10 h-10 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
          </a>
        </div>
      </section>

      {/* What to do while you wait */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">Before we arrive</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Three quick things <br /> to do right now.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">Stay calm. These three things take 60 seconds and make the on-site work much faster.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-border">
            {[
              { t: "Make it safe", b: "Water: shut off the mains. Power: kill the breaker. Lockout: don't force the door — wait for us.", img: SERVICE_BY_SLUG.plumbing.image },
              { t: "Take a photo", b: "Snap the damage, the meter, the broken part. Helps us prep the right gear before we leave.", img: SERVICE_BY_SLUG.electrical.image },
              { t: "Clear the path", b: "Move cars off the driveway, unlock the gate, secure pets. Speeds up the on-site work.", img: SERVICE_BY_SLUG.locksmithing.image },
            ].map((s, i) => (
              <div key={i} data-testid={`emergency-tip-${i}`} className="relative group bg-card border-r border-b border-border overflow-hidden flex flex-col">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground/40 to-transparent" />
                  <div className="absolute top-4 left-4 font-display text-4xl font-extrabold text-white drop-shadow leading-none tabular-nums">0{i + 1}</div>
                </div>
                <div className="p-7 flex flex-col gap-3">
                  <h3 className="font-display text-xl font-bold tracking-tight">{s.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
